import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PhoneIcon, MapPinIcon, MailIcon } from 'lucide-react';
const serviceOptions = ['Kitchen Renovation', 'Bathroom Renovation', 'Full House Renovation', 'Flooring & Painting', 'Plumbing & Electrical', 'Roofing Renovation', 'New Builds'];
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    services: [] as string[],
    message: ''
  });
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service) ? prev.services.filter(s => s !== service) : [...prev.services, service]
    }));
  };
  return <section id="contact" className="py-12 sm:py-16 md:py-20 bg-[#0f0f0f]" ref={ref} data-id="element-86">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-87">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-10 sm:mb-12 md:mb-16" data-id="element-88">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4" data-id="element-89">
            Get in <span className="text-[#d4af37]" data-id="element-90">Touch</span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto px-4" data-id="element-91">
            Ready to transform your home? Contact us today for a free quote
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12" data-id="element-92">
          {/* Left - Form */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8
        }} data-id="element-93">
            <form action="https://formsubmit.co/info@renova8build.com.au" method="POST" className="space-y-4 sm:space-y-6" data-id="element-94">
              {/* FormSubmit Configuration */}
              <input type="hidden" name="_subject" value="New Contact Form Submission - Renova8 & Build" data-id="element-95" />
              <input type="hidden" name="_captcha" value="false" data-id="element-96" />
              <input type="hidden" name="_template" value="table" data-id="element-97" />
              <input type="hidden" name="services" value={formData.services.join(', ') || 'None selected'} data-id="element-98" />
              <div data-id="element-99">
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name" required className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#1c1c1c] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] transition-all text-sm sm:text-base" data-id="element-100" />
              </div>
              <div data-id="element-101">
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Your Email" required className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#1c1c1c] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] transition-all text-sm sm:text-base" data-id="element-102" />
              </div>
              <div data-id="element-103">
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Your Phone" required className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#1c1c1c] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] transition-all text-sm sm:text-base" data-id="element-104" />
              </div>
              {/* Services Selection */}
              <div className="bg-[#1c1c1c] p-4 sm:p-6 rounded-lg" data-id="element-105">
                <label className="text-white font-semibold mb-3 block text-sm sm:text-base" data-id="element-106">
                  Services Interested In:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-107">
                  {serviceOptions.map(service => <label key={service} className="flex items-center gap-2 cursor-pointer group" data-id="element-108">
                      <input type="checkbox" checked={formData.services.includes(service)} onChange={() => handleServiceChange(service)} className="w-4 h-4 rounded border-gray-600 bg-[#0f0f0f] text-[#d4af37] focus:ring-2 focus:ring-[#d4af37] focus:ring-offset-0 cursor-pointer" data-id="element-109" />
                      <span className="text-gray-400 group-hover:text-[#d4af37] transition-colors text-sm" data-id="element-110">
                        {service}
                      </span>
                    </label>)}
                </div>
              </div>
              <div data-id="element-111">
                <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Your Message" required rows={5} className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#1c1c1c] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] transition-all resize-none text-sm sm:text-base" data-id="element-112" />
              </div>
              <button type="submit" className="w-full bg-[#d4af37] text-[#1c1c1c] py-3 sm:py-4 rounded-lg font-semibold hover:bg-[#4b4bff] hover:text-white transition-all duration-300 transform hover:scale-105 active:scale-95 text-sm sm:text-base touch-manipulation" data-id="element-113">
                Send Message
              </button>
            </form>
          </motion.div>
          {/* Right - Contact Info & Map */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="space-y-6 sm:space-y-8" data-id="element-114">
            <div className="bg-[#1c1c1c] p-4 sm:p-6 rounded-lg" data-id="element-115">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-116">
                <PhoneIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37] flex-shrink-0 mt-1" data-id="element-117" />
                <div data-id="element-118">
                  <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base" data-id="element-119">
                    Phone
                  </h3>
                  <a href="tel:+61721004589" className="text-gray-400 hover:text-[#d4af37] transition-colors text-sm sm:text-base" data-id="element-120">
                    +61 7 2100 4589
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-121">
                <MailIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37] flex-shrink-0 mt-1" data-id="element-122" />
                <div data-id="element-123">
                  <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base" data-id="element-124">
                    Email
                  </h3>
                  <a href="mailto:info@renova8build.com.au" className="text-gray-400 hover:text-[#d4af37] transition-colors text-sm sm:text-base break-all" data-id="element-125">
                    info@renova8build.com.au
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-126">
                <MapPinIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37] flex-shrink-0 mt-1" data-id="element-127" />
                <div data-id="element-128">
                  <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base" data-id="element-129">
                    Address
                  </h3>
                  <p className="text-gray-400 text-sm sm:text-base" data-id="element-130">
                    48 O'Keefe St, Woolloongabba QLD 4102, Australia
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4" data-id="element-131">
                <MailIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37] flex-shrink-0 mt-1" data-id="element-132" />
                <div data-id="element-133">
                  <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base" data-id="element-134">
                    Website
                  </h3>
                  <a href="https://renova8build.com.au" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#d4af37] transition-colors text-sm sm:text-base break-all" data-id="element-135">
                    renova8build.com.au
                  </a>
                </div>
              </div>
            </div>
            {/* Google Maps Embed */}
            <div className="h-[250px] sm:h-[300px] rounded-lg overflow-hidden" data-id="element-136">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3540.0845!2d153.0397!3d-27.4925!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b915a1d0c0d0d0d%3A0x0!2s48%20O'Keefe%20St%2C%20Woolloongabba%20QLD%204102!5e0!3m2!1sen!2sau!4v1234567890" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-137" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}